import type {
  GameCommand,
  GameEvent,
  GameState,
  ReplayFile,
  RuntimeSnapshot,
  GameEngine,
  TowerState,
  EnemyState,
  WavePreview,
  WavePreviewEnemyGroup,
} from "@bastion/game-core";
import {
  saveGame as _saveGame,
  loadGame as _loadGame,
  deleteSave as _deleteSave,
  listSaves as _listSaves,
  recordGame as _recordGame,
  getStats as _getStats,
  resetStats as _resetStats,
  type SessionStats,
  type AudioSystem,
  checkAchievements as _checkAchievements,
  type AchievementContext,
  getTowerLevelStats,
  scaleEnemyStats,
} from "@bastion/game-core";
import type { GameContent, TowerSkillKey, WaveGroup, EnemyConfig } from "@bastion/data";
import { assetManifest, resolveAssetUrls } from "@bastion/game-renderer";
import { subscribeGameAudioEvents, SfxId } from "./replayAudio";
export { type SfxId } from "./replayAudio";

/**
 * 唯一的类型化门面，React 通过它与游戏交互（CLAUDE.md §22）。React 从不
 * 直接触摸引擎的可变状态——它调用这些方法，这些方法内部会 dispatch 命令，
 * 并且它会订阅每个渲染帧的状态快照和事件流。
 *
 * 这保持了数据流的单向性：
 *
 *   React 按钮 → facade.method() → engine.dispatch(command)
 *   engine 事件  → facade 监听器 → React 状态（每个渲染帧）
 */
export interface SkillHudState {
  id: string;
  cost: number;
  cooldown: number;
  /** 按游戏模拟时间计算的剩余冷却秒数。 */
  cooldownRemaining: number;
  ready: boolean;
  /** 技能作用半径（为 null 表示无范围限制，直接释放无需瞄准） */
  radius: number | null;
}

export interface TowerSkillHudState {
  id: string;
  description?: TowerSkillKey;
  icon?: string;
  progress: number;
  threshold: number;
  ready: boolean;
  active: boolean;
}

export interface HudSnapshot {
  gold: number;
  lives: number;
  maxLives: number;
  wave: number;
  totalWaves: number;
  /** 是否为无尽模式 */
  isEndless: boolean;
  waveInProgress: boolean;
  waveEnemiesRemaining: number;
  waveEnemiesTotal: number;
  /** 距离下一波自动开始剩余的秒数（若无倒计时则为 0） */
  waveCountdown: number;
  status: GameState["status"];
  gameSpeed: number;
  /** 每秒帧数，每个渲染帧快照更新 */
  fps: number;
  /** 玩家可主动释放的全局技能状态，与本快照其余 HUD 数据同一时点读取。 */
  skills: SkillHudState[];
  /** 当前页面是否为调试模式。 */
  isDebug: boolean;
  /** 调试页使用无限金币；正式页为 false。 */
  unlimitedGold: boolean;
  /** 调试页可放置的全部敌人类型。 */
  enemyTypes: Array<{ id: string; isBoss: boolean; skinUrl?: string }>;
  /** 玩家可建造的可用塔类型（id + 费用 + 皮肤贴图 URL） */
  towerTypes: Array<{ id: string; cost: number; skinUrl?: string }>;
  /** 当前场上的塔 */
  towers: Array<{
    id: string;
    type: string;
    level: number;
    x: number;
    y: number;
    /** 升级到下一级的金币费用；已处于最高级时为 null */
    upgradeCost: number | null;
    /** 建造时的初始费用 */
    buildCost: number;
    /** 每次升级的花费列表 */
    upgradeCosts: number[];
  }>;
  /** UI 当前选中的塔（无选中时为 null） */
  selectedTowerId: string | null;
  /** 选中塔的地图坐标（用于在画布上定位操作栏） */
  selectedTowerPos: { x: number; y: number } | null;
  /** UI 当前选中的敌人（无选中时为 null） */
  selectedEnemyId: string | null;
  /** 选中敌人的当前 HP/最大 HP（在 UI 中显示） */
  selectedEnemyHp?: { current: number; max: number };
  /** 选中塔的配置文件类型 ID（用于查找塔配置） */
  selectedTowerType: string | null;
  /** 选中塔的等级（从 1 开始，仅在 selectedTowerType 设置时有效） */
  selectedTowerLevel?: number;
  /** 选中炮台关联的自动技能及各自释放状态。 */
  selectedTowerSkills: TowerSkillHudState[];
  /** 选中塔的当前实时属性（包含等级覆盖和临时攻速增益） */
  selectedTowerStats?: {
    attack: number;
    magic: number;
    defense: number;
    magicResistance: number;
    accuracy: number;
    resistance: number;
    baseAttackSpeed: number;
    attackSpeed: number;
    attackSpeedMultiplier: number;
    activeAttackSpeedBoosts: Array<{ factor: number; remaining: number }>;
    critRate: number;
    critDamage: number;
    range: number;
    splashRadius: number;
    chainCount: number;
  };
  /** 选中塔升级费用；已处于最高级时为 null */
  selectedTowerUpgradeCost: number | null;
  /** 选中敌人的配置文件类型 ID（用于查找敌人配置） */
  selectedEnemyType: string | null;
}

export type BuildMode = { kind: "tower" | "enemy"; type: string };

export interface GameFacade {
  /** 可选的音频系统；使用前应检查 isAvailable。 */
  audioSystem: AudioSystem | null;
  /** 按 id 程序化播放声音；不可用时为安全的不操作。 */
  playSound(soundId: SfxId): void;
  /** 设置主音量 0..1；无音频系统时不操作。 */
  setMasterVolume(volume: number): void;
  /** 当前活跃的音频 provider 是否能发声。 */
  isAudioAvailable(): boolean;
  /** 必须由用户手势调用以解锁浏览器音频输出。 */
  unlockAudio(): Promise<boolean>;
  start(mapId: string): void;
  buildTower(towerType: string, position: { x: number; y: number }): void;
  buildEnemyDebug(enemyType: string, position: { x: number; y: number }): void;
  upgradeTower(towerId: string): void;
  sellTower(towerId: string): void;
  startWave(): void;
  pause(): void;
  resume(): void;
  setSpeed(speed: 1 | 2 | 4): void;
  useSkill(skillId: string, position?: { x: number; y: number }): void;
  /** 订阅每个渲染帧的 HUD 快照；返回取消订阅函数 */
  onHudSnapshot(listener: (snapshot: HudSnapshot) => void): () => void;
  /** 订阅指定游戏事件；返回取消订阅函数 */
  onEvent(type: GameEvent["type"], listener: (event: GameEvent) => void): () => void;
  /** 订阅任意事件 */
  onAnyEvent(listener: (event: GameEvent) => void): () => void;
  /** 请求渲染器（Phaser）进入/退出建造放置模式 */
  setBuildMode(mode: BuildMode | null): void;
  /** 请求渲染器更新放置幽灵以匹配塔类型身体半径 */
  setPlacementRadius(towerType: string | null): void;
  /** 更新瞬态放置幽灵在逻辑地图坐标中的位置 */
  setPlacementPosition(position: { x: number; y: number } | null): void;
  /** 开始技能瞄准模式：渲染器显示范围圈，点击画布触发技能 */
  startSkillAim(skillId: string): void;
  /** 取消技能瞄准模式 */
  cancelSkillAim(): void;
  /** 请求渲染器高亮一座塔 */
  selectTower(towerId: string | null): void;
  /** 当塔被选中时，向渲染器传递操作按钮数据 */
  setOnTowerSelected(
    hook:
      | ((data: {
          id: string;
          type: string;
          position: { x: number; y: number };
          buildCost: number;
          upgradeCosts: number[];
        }) => void)
      | null,
  ): void;
  /** 请求渲染器高亮一个敌人 */
  selectEnemy(enemyId: string | null): void;
  getTowerTypes(): Array<{ id: string; cost: number }>;
  getEnemyTypes(): Array<{ id: string; isBoss: boolean }>;
  /** 按需计算的当前 HUD 快照 */
  snapshotNow(): HudSnapshot;
  /** 不推进游戏状态的只读波次预览 */
  getWavePreview(mapId: string, waveIndex: number): WavePreview | null;
  getSkillStates(): SkillHudState[];
  /** 导出完整运行态快照；包含 facade 持有的局内成就计数。 */
  exportRuntimeSnapshot(): RuntimeSnapshot;
  /** 仅在当前地图首波开始前导入，并在恢复后暂停。 */
  importRuntimeSnapshot(snapshot: RuntimeSnapshot): void;
  /** 当前地图是否仍处于首波开始前的准备阶段。 */
  canImportRuntimeSnapshot(): boolean;
  exportReplayFile(): ReplayFile;
  canStartReplay(): boolean;
  isReplayMode(): boolean;
  /** 将当前游戏状态持久化到 localStorage 中 mapId 键下 */
  saveGame(mapId: string): void;
  /** 从 localStorage 恢复游戏状态；若找到存档则返回 true */
  loadGame(mapId: string): boolean;
  /** 移除存档槽；若存在则返回 true */
  deleteSave(mapId: string): boolean;
  /** 列出所有存档槽（仅元数据） */
  listSaves(): Array<{ mapId: string; savedAt: number; wave: number; gold: number; lives: number }>;
  /** 获取聚合会话统计数据 */
  getSessionStats(): SessionStats;
  /** 将已完成的游戏结果记录到会话统计中 */
  recordGameResult(result: {
    status: GameState["status"];
    wave: number;
    goldEarned: number;
    enemiesKilled: number;
  }): void;
  /** 重置所有会话统计数据 */
  resetSessionStats(): void;
  /** 当前会话中累计建造的塔数（用于成就统计） */
  getTowersBuiltSession(): number;
  /** 本会话中到达基地的敌人累计损失的生命值（用于成就统计） */
  getLivesLostThisGame(): number;
  /** 第 1 波完成时的模拟时间（秒）；若尚未完成则为 0，用于成就统计 */
  getWave1CompleteTime(): number;
  /** 当前经过的模拟时间（秒）（用于成就统计） */
  getElapsedTime(): number;
  /** 获取引擎当前使用的地图 id；若尚未开始任何游戏则返回 null */
  getCurrentMapId(): string | null;
  /** 列出内容包中所有可用的地图 */
  getAvailableMaps(): Array<{ id: string; totalWaves: number }>;
  /** 使用当前游戏状态手动触发成就评估。 */
  checkAchievements(): string[];
  dispose(): void;
}

export class GameFacadeImpl implements GameFacade {
  private readonly engine: GameEngine;
  private readonly content: GameContent;
  private readonly audioSystem_: AudioSystem | null;
  private buildModeHook: ((mode: BuildMode | null) => void) | null = null;
  private placementRadiusHook: ((towerType: string | null) => void) | null = null;
  private placementPositionHook: ((position: { x: number; y: number } | null) => void) | null =
    null;
  private selectTowerHook: ((towerId: string | null) => void) | null = null;
  private selectEnemyHook: ((enemyId: string | null) => void) | null = null;
  /** 技能瞄准模式开始/取消回调（由 BastionApp 注入，驱动渲染器显示/隐藏范围圈）。 */
  private skillAimStartHook: ((skillId: string) => void) | null = null;
  private skillAimCancelHook: (() => void) | null = null;
  /** 当塔被选中时，向渲染器传递操作按钮所需数据。 */
  private onTowerSelectedHook:
    | ((data: {
        id: string;
        type: string;
        position: { x: number; y: number };
        buildCost: number;
        upgradeCosts: number[];
        level: number;
      }) => void)
    | null = null;
  private unsubs: Array<() => void> = [];
  private disposed = false;
  private selectedTowerId: string | null = null;
  private selectedEnemyId: string | null = null;
  /** FPS 追踪 */
  private _fpsFrames = 0;
  private _fpsLastTime = performance.now();
  private _fpsValue = 0;
  /** 本会话中从波次完成获得的累计金币。 */
  private _cumulativeGoldEarned = 0;
  /** 本会话中累计击杀的敌人数。 */
  private _enemiesKilledThisSession = 0;
  private replayMode = false;
  /** 炮台皮肤贴图 URL，key 为 tower type id */
  private readonly towerSkinUrls: Record<string, string>;
  /** 怪物皮肤贴图 URL，key 为 enemy type id */
  private readonly enemySkinUrls: Record<string, string>;

  constructor(
    engine: GameEngine,
    content: GameContent,
    hooks: {
      setBuildMode?: (mode: BuildMode | null) => void;
      setPlacementRadius?: (towerType: string | null) => void;
      setPlacementPosition?: (position: { x: number; y: number } | null) => void;
      selectTower?: (towerId: string | null) => void;
      selectEnemy?: (enemyId: string | null) => void;
      onTowerSelected?: (data: {
        id: string;
        type: string;
        position: { x: number; y: number };
        buildCost: number;
        upgradeCosts: number[];
        level: number;
      }) => void;
      /** 开始技能瞄准模式 */
      startSkillAim?: (skillId: string) => void;
      /** 取消技能瞄准模式 */
      cancelSkillAim?: () => void;
    } = {},
    audioSystem: AudioSystem | null = null,
  ) {
    this.engine = engine;
    this.content = content;
    this.audioSystem_ = audioSystem;
    this.buildModeHook = hooks.setBuildMode ?? null;
    this.placementRadiusHook = hooks.setPlacementRadius ?? null;
    this.placementPositionHook = hooks.setPlacementPosition ?? null;
    this.selectTowerHook = hooks.selectTower ?? null;
    this.selectEnemyHook = hooks.selectEnemy ?? null;
    this.onTowerSelectedHook = hooks.onTowerSelected ?? null;
    this.skillAimStartHook = hooks.startSkillAim ?? null;
    this.skillAimCancelHook = hooks.cancelSkillAim ?? null;
    this.attachAudioEvents();
    // 构建皮肤贴图 URL 映射
    const resolvedUrls =
      typeof document !== "undefined" ? resolveAssetUrls(assetManifest, document.baseURI) : {};
    this.towerSkinUrls = Object.fromEntries(
      Object.entries(resolvedUrls).filter(([k]) => k.startsWith("tower.")),
    );
    this.enemySkinUrls = Object.fromEntries(
      Object.entries(resolvedUrls).filter(([k]) => k.startsWith("enemy.")),
    );
  }

  get audioSystem(): AudioSystem | null {
    return this.audioSystem_;
  }

  isAudioAvailable(): boolean {
    return this.audioSystem_?.isAvailable() ?? false;
  }

  unlockAudio(): Promise<boolean> {
    return this.audioSystem_?.unlock?.() ?? Promise.resolve(false);
  }

  playSound(soundId: SfxId): void {
    this.audioSystem_?.playSound(soundId);
  }

  setMasterVolume(volume: number): void {
    this.audioSystem_?.setMasterVolume(volume);
  }

  private attachAudioEvents(): void {
    if (this.audioSystem_) {
      this.unsubs.push(subscribeGameAudioEvents(this.engine, this.audioSystem_, this.content));
    }
    this.unsubs.push(
      this.engine.on("game_started", () => {
        this._cumulativeGoldEarned = 0;
        this._enemiesKilledThisSession = 0;
      }),
      this.engine.on("enemy_killed", () => {
        this._enemiesKilledThisSession += 1;
        this._doCheckAchievements();
      }),
      this.engine.on("wave_completed", () => this._doCheckAchievements()),
      this.engine.on("wave_reward_granted", (event) => {
        const payload = event.payload as Record<string, unknown> | undefined;
        const rewardGold = Number(payload?.rewardGold ?? 0);
        if (!isNaN(rewardGold) && rewardGold > 0) this._cumulativeGoldEarned += rewardGold;
        this._doCheckAchievements();
      }),
      this.engine.on("game_won", () => this._doCheckAchievements(true)),
    );
  }

  /** 根据当前引擎状态构建 AchievementContext 并发出新解锁的成就。 */
  private _doCheckAchievements(finalWaveCompleted = false): void {
    const ctx: AchievementContext = {
      enemiesKilled: this._enemiesKilledThisSession,
      currentWave: this.engine.state.wave,
      towersBuiltTotal: this.getTowersBuiltSession(),
      goldEarned: this._cumulativeGoldEarned,
      finalWaveCompleted,
      livesLostThisGame: this.getLivesLostThisGame(),
      wave1CompleteTime: this.getWave1CompleteTime(),
      elapsedTime: this.getElapsedTime(),
    };
    const newlyUnlocked = _checkAchievements(ctx, this.getTowersBuiltSession());
    if (newlyUnlocked.length > 0) {
      this.engine.events.emit({
        type: "achievement_unlocked",
        time: this.engine.time,
        payload: { ids: newlyUnlocked },
      });
    }
  }

  /**
   * 成就系统的公共入口点。
   * 在 enemy_killed、wave_completed 和 game_won 时自动调用，
   * 但也在此暴露以便外部代码（如测试、GM 命令）触发它。
   */
  checkAchievements(): string[] {
    const ctx: AchievementContext = {
      enemiesKilled: this._enemiesKilledThisSession,
      currentWave: this.engine.state.wave,
      towersBuiltTotal: this.getTowersBuiltSession(),
      goldEarned: this._cumulativeGoldEarned,
      finalWaveCompleted: false,
      livesLostThisGame: this.getLivesLostThisGame(),
      wave1CompleteTime: this.getWave1CompleteTime(),
      elapsedTime: this.getElapsedTime(),
    };
    return _checkAchievements(ctx, this.getTowersBuiltSession());
  }

  start(mapId: string): void {
    this.engine.start(mapId);
  }

  buildTower(towerType: string, position: { x: number; y: number }): void {
    this.dispatch({ type: "build_tower", towerType, position });
  }

  buildEnemyDebug(enemyType: string, position: { x: number; y: number }): void {
    this.dispatch({ type: "build_enemy_debug", enemyType, position });
  }

  upgradeTower(towerId: string): void {
    this.dispatch({ type: "upgrade_tower", towerId });
  }

  sellTower(towerId: string): void {
    this.dispatch({ type: "sell_tower", towerId });
  }

  startWave(): void {
    if (this.engine.state.status === "paused") {
      this.dispatch({ type: "resume_game" });
    }
    this.dispatch({ type: "start_wave" });
  }

  pause(): void {
    if (this.engine.state.status !== "running") return;
    this.dispatch({ type: "pause_game" });
  }

  resume(): void {
    if (this.engine.state.status !== "paused") return;
    this.dispatch({ type: "resume_game" });
  }

  setSpeed(speed: 1 | 2 | 4): void {
    this.dispatch({ type: "set_game_speed", speed });
  }

  useSkill(skillId: string, position?: { x: number; y: number }): void {
    this.dispatch({ type: "use_skill", skillId, position });
  }

  onHudSnapshot(listener: (snapshot: HudSnapshot) => void): () => void {
    let frameId: number | null = null;
    const update = () => {
      if (this.disposed) return;
      this._fpsFrames++;
      const now = performance.now();
      if (now - this._fpsLastTime >= 1000) {
        this._fpsValue = this._fpsFrames;
        this._fpsFrames = 0;
        this._fpsLastTime = now;
      }
      listener(this.snapshotNow());
      frameId = requestAnimationFrame(update);
    };
    frameId = requestAnimationFrame(update);

    const unsub = () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      this.disposed = true;
    };
    this.unsubs.push(unsub);
    return unsub;
  }

  onEvent(type: GameEvent["type"], listener: (event: GameEvent) => void): () => void {
    return this.engine.on(type, listener);
  }

  onAnyEvent(listener: (event: GameEvent) => void): () => void {
    const types: GameEvent["type"][] = [
      "game_started",
      "game_won",
      "game_lost",
      "tower_built",
      "tower_upgraded",
      "tower_sold",
      "enemy_spawned",
      "enemy_damaged",
      "enemy_killed",
      "wave_started",
      "wave_completed",
      "wave_reward_granted",
      "boss_spawned",
      "player_damaged",
      "game_paused",
      "game_resumed",
      "game_speed_changed",
      "skill_used",
      "skill_cooldown_started",
      "achievement_unlocked",
    ];
    const unsubs = types.map((t) => this.engine.on(t, listener));
    const unsub = () => unsubs.forEach((u) => u());
    this.unsubs.push(unsub);
    return unsub;
  }

  setBuildMode(mode: BuildMode | null): void {
    if (this.replayMode) return;
    this.buildModeHook?.(mode);
  }

  setPlacementRadius(towerType: string | null): void {
    if (this.replayMode) return;
    this.placementRadiusHook?.(towerType);
  }

  setPlacementPosition(position: { x: number; y: number } | null): void {
    if (this.replayMode) return;
    this.placementPositionHook?.(position);
  }

  startSkillAim(skillId: string): void {
    this.skillAimStartHook?.(skillId);
  }

  cancelSkillAim(): void {
    this.skillAimCancelHook?.();
  }

  selectTower(towerId: string | null): void {
    this.selectedTowerId = towerId;
    this.selectedEnemyId = null;
    // 先向渲染器传递选中塔的详细信息（用于绘制操作按钮）
    if (towerId && this.onTowerSelectedHook) {
      const tower = this.engine.state.towers.find((t) => t.id === towerId);
      if (tower) {
        this.onTowerSelectedHook({
          id: tower.id,
          type: tower.type,
          position: tower.position,
          buildCost: tower.buildCost,
          upgradeCosts: tower.upgradeCosts,
          level: tower.level,
        });
      }
    }
    // 然后请求渲染器高亮塔
    this.selectTowerHook?.(towerId);
  }

  setOnTowerSelected(
    hook:
      | ((data: {
          id: string;
          type: string;
          position: { x: number; y: number };
          buildCost: number;
          upgradeCosts: number[];
        }) => void)
      | null,
  ): void {
    this.onTowerSelectedHook = hook;
  }

  selectEnemy(enemyId: string | null): void {
    this.selectedEnemyId = enemyId;
    this.selectedTowerId = null;
    this.selectEnemyHook?.(enemyId);
  }

  getTowerTypes(): Array<{ id: string; cost: number; skinUrl?: string }> {
    return Object.values(this.content.towers).map((t) => ({
      id: t.id,
      cost: t.cost,
      skinUrl: this.towerSkinUrls[`tower.${t.id}`],
    }));
  }

  getEnemyTypes(): Array<{ id: string; isBoss: boolean; skinUrl?: string }> {
    return [...Object.values(this.content.enemies), ...Object.values(this.content.bosses)].map(
      (enemy: EnemyConfig) => ({
        id: enemy.id,
        isBoss: enemy.isBoss === true,
        skinUrl: this.enemySkinUrls[`enemy.${enemy.id}`],
      }),
    );
  }

  getSkillStates(): SkillHudState[] {
    const now = this.engine.time;
    // 只返回全局技能（玩家可手动触发）；炮塔技能由充能系统自动释放，不在此展示。
    return Object.values(this.content.skills)
      .filter((skill) => skill.type === "GLOBAL")
      .map((skill) => {
        const cooldown = this.engine.state.skillCooldowns.find((s) => s.id === skill.id);
        const cooldownRemaining = this.engine.isDebugMode
          ? 0
          : Math.max(0, (cooldown?.cooldownEnd ?? now) - now);
        return {
          id: skill.id,
          cost: skill.cost,
          cooldown: skill.cooldown,
          cooldownRemaining,
          ready: cooldownRemaining === 0,
          radius: skill.radius ?? null,
        };
      });
  }

  /** 不推进游戏状态的只读波次预览 */
  getWavePreview(mapId: string, waveIndex: number): WavePreview | null {
    const map = this.content.maps[mapId];
    if (!map) return null;
    const waveId = map.waves[waveIndex];
    if (!waveId) return null;
    const waveConfig = this.content.waves[waveId];
    if (!waveConfig) return null;

    const groups: WavePreviewEnemyGroup[] = waveConfig.groups
      .map((g: WaveGroup) => {
        const enemyConfig = this.content.enemies[g.enemyId] ?? this.content.bosses[g.enemyId];
        if (!enemyConfig) return null;
        const scaling = scaleEnemyStats(enemyConfig, map, this.engine.getBalance(), waveIndex);
        return {
          enemyId: g.enemyId,
          isBoss: enemyConfig.isBoss === true,
          count: g.count,
          spawnInterval: g.spawnInterval,
          delay: g.delay,
          estimatedHp: scaling.maxHp,
          estimatedAttack: scaling.attack,
          estimatedMagic: scaling.magic,
          estimatedDefense: scaling.defense,
          estimatedMagicResistance: scaling.magicResistance,
          estimatedSpeed: enemyConfig.stats.speed,
        };
      })
      .filter((g): g is WavePreviewEnemyGroup => g !== null);

    let totalSpawnDuration = 0;
    for (const g of waveConfig.groups) {
      const groupEnd = g.delay + Math.max(0, g.count - 1) * g.spawnInterval;
      if (groupEnd > totalSpawnDuration) totalSpawnDuration = groupEnd;
    }

    return {
      wave: waveConfig.wave,
      waveIndex,
      groups: groups,
      totalEnemies: waveConfig.groups.reduce((sum, g) => sum + g.count, 0),
      totalSpawnDuration,
      rewardGold: waveConfig.rewardGold,
      isFinal: waveConfig.isFinal === true,
    };
  }

  exportRuntimeSnapshot(): RuntimeSnapshot {
    return this.engine.createRuntimeSnapshot({
      enemiesKilled: this._enemiesKilledThisSession,
      goldEarned: this._cumulativeGoldEarned,
    });
  }

  importRuntimeSnapshot(snapshot: RuntimeSnapshot): void {
    if (!this.canImportRuntimeSnapshot()) {
      throw new Error("只有第 0 波开始前才能导入游戏进度");
    }
    if (this.engine.getCurrentMapId() !== snapshot.mapId) {
      throw new Error("无法导入：文件地图与当前地图不匹配");
    }
    this.engine.restoreRuntimeSnapshot(snapshot);
    this._enemiesKilledThisSession = snapshot.session.enemiesKilled;
    this._cumulativeGoldEarned = snapshot.session.goldEarned;
    this.selectedTowerId = null;
    this.selectedEnemyId = null;
    this.selectTowerHook?.(null);
    this.selectEnemyHook?.(null);
  }

  exportReplayFile(): ReplayFile {
    if (this.replayMode) throw new Error("回放模式不支持再次导出录制");
    return this.engine.createReplayFile();
  }

  canStartReplay(): boolean {
    return !this.replayMode && this.engine.state.status === "running";
  }

  isReplayMode(): boolean {
    return this.replayMode;
  }

  setReplayMode(enabled: boolean): void {
    this.replayMode = enabled;
    this.selectedTowerId = null;
    this.selectedEnemyId = null;
    if (enabled) {
      this._cumulativeGoldEarned = 0;
      this._enemiesKilledThisSession = 0;
      this.audioSystem_?.stopAllSounds();
    }
  }

  canImportRuntimeSnapshot(): boolean {
    return (
      this.engine.getCurrentMapId() !== null &&
      this.engine.state.wave === 0 &&
      !this.engine.state.waveInProgress
    );
  }

  saveGame(mapId: string): void {
    const gameState = {
      ...this.engine.state,
      pendingOrbitalStrikes: this.engine.state.pendingOrbitalStrikes ?? [],
      queuedGlobalSkills: this.engine.state.queuedGlobalSkills ?? [],
    };
    _saveGame(mapId, gameState, _getStats());
  }

  loadGame(mapId: string): boolean {
    const slot = _loadGame(mapId);
    if (!slot) return false;
    // 逐个恢复状态字段，以免替换引擎的内部时钟或随机实例——仅恢复玩家可见的状态。
    const s = this.engine.state;
    s.status = slot.gameState.status;
    s.time = slot.gameState.time;
    s.gold = slot.gameState.gold;
    s.baseHp = slot.gameState.baseHp;
    s.maxHp = slot.gameState.maxHp;
    s.wave = slot.gameState.wave;
    s.totalWaves = slot.gameState.totalWaves;
    s.waveInProgress = slot.gameState.waveInProgress;
    const legacySlot = slot as typeof slot & { schemaVersion?: number };
    const isLegacy = (legacySlot.schemaVersion ?? 0) < 2;
    s.towers = slot.gameState.towers.map((tower) => {
      type LegacyTowerState = Omit<TowerState, "skillReleaseProgress" | "pendingAttackSkillId"> & {
        skillReleaseProgress?: Record<string, number> | number;
        chargeCurrent?: number;
        chargeRequired?: number;
        pendingAttackMode?: "ice_cone";
        pendingAttackSkillId?: string;
      };
      const legacy = tower as LegacyTowerState;
      const skillIds = this.content.towers[legacy.type]?.skillIds ?? [];
      const storedProgress = legacy.skillReleaseProgress;
      const nextProgress: Record<string, number> = {};
      for (const id of skillIds) {
        const condition = this.content.skills[id]?.releaseCondition;
        if (condition?.kind !== "attack_count") continue;
        const stored =
          typeof storedProgress === "object" && storedProgress !== null
            ? storedProgress[id]
            : undefined;
        nextProgress[id] = Math.min(Math.max(0, stored ?? 0), condition.count);
      }
      if (isLegacy && typeof storedProgress === "number") {
        const firstTowerSkill = skillIds.find((id) => this.content.skills[id]?.type === "TOWER");
        if (firstTowerSkill) {
          const condition = this.content.skills[firstTowerSkill]?.releaseCondition;
          nextProgress[firstTowerSkill] =
            condition?.kind === "attack_count"
              ? Math.min(Math.max(0, storedProgress), condition.count)
              : 0;
        }
      } else if (isLegacy && legacy.chargeCurrent !== undefined) {
        const firstTowerSkill = skillIds.find((id) => this.content.skills[id]?.type === "TOWER");
        if (firstTowerSkill) {
          const condition = this.content.skills[firstTowerSkill]?.releaseCondition;
          nextProgress[firstTowerSkill] =
            condition?.kind === "attack_count"
              ? Math.min(Math.max(0, legacy.chargeCurrent), condition.count)
              : 0;
        }
      }
      const pendingAttackSkillId =
        legacy.pendingAttackSkillId &&
        skillIds.includes(legacy.pendingAttackSkillId) &&
        this.content.skills[legacy.pendingAttackSkillId]?.nextAttack !== undefined
          ? legacy.pendingAttackSkillId
          : isLegacy && legacy.pendingAttackMode
            ? skillIds.find((id) => this.content.skills[id]?.nextAttack !== undefined)
            : undefined;

      const {
        chargeCurrent: _legacyChargeCurrent,
        chargeRequired: _legacyChargeRequired,
        pendingAttackMode: _legacyPendingAttackMode,
        ...normalizedTower
      } = legacy;
      return {
        ...normalizedTower,
        skillReleaseProgress: nextProgress,
        pendingAttackSkillId,
        attackSpeedBoosts: (legacy.attackSpeedBoosts ?? []).map((boost) => ({
          ...boost,
          skillId:
            boost.skillId && skillIds.includes(boost.skillId)
              ? boost.skillId
              : skillIds.find((id) => this.content.skills[id]?.attackSpeedFactor === boost.factor),
        })),
      };
    });
    s.enemies = slot.gameState.enemies.map((enemy) => {
      const config = this.content.enemies[enemy.type] ?? this.content.bosses[enemy.type];
      const map = this.content.maps[mapId];
      const waveIndexFromMap =
        map?.waves.findIndex(
          (waveId) => this.content.waves[waveId]?.wave === slot.gameState.wave,
        ) ?? -1;
      const waveIndex =
        enemy.waveIndex ??
        (waveIndexFromMap >= 0
          ? waveIndexFromMap
          : map?.isEndless
            ? Math.max(map.waves.length, slot.gameState.wave - 1)
            : Math.max(0, slot.gameState.wave - 1));
      const scaledStats =
        config && map
          ? scaleEnemyStats(config, map, this.engine.getBalance(), waveIndex)
          : undefined;
      const skillIds = config?.skillIds ?? [];
      const legacy = enemy as EnemyState & {
        skillTimers?: Record<string, number>;
        skillTimerRemaining?: number;
        skillTimerInterval?: number;
      };
      const timers: Record<string, number> = {};
      const firstMonsterSkill = skillIds.find((id) => this.content.skills[id]?.type === "MONSTER");
      for (const id of skillIds) {
        const condition = this.content.skills[id]?.releaseCondition;
        if (condition?.kind !== "interval") continue;
        const storedTimer = legacy.skillTimers?.[id];
        timers[id] =
          storedTimer !== undefined
            ? storedTimer
            : isLegacy && id === firstMonsterSkill && legacy.skillTimerRemaining !== undefined
              ? legacy.skillTimerRemaining
              : (condition.initialDelay ?? condition.seconds);
      }
      const {
        skillTimerRemaining: _skillTimerRemaining,
        skillTimerInterval: _skillTimerInterval,
        ...normalizedEnemy
      } = legacy;
      return {
        ...normalizedEnemy,
        attack: legacy.attack ?? scaledStats?.attack,
        magic: legacy.magic ?? scaledStats?.magic,
        defense: legacy.defense ?? scaledStats?.defense,
        magicResistance: legacy.magicResistance ?? scaledStats?.magicResistance,
        skillTimers: timers,
      };
    });
    s.projectiles = slot.gameState.projectiles;
    s.gameSpeed = slot.gameState.gameSpeed;
    s.entityCounter = slot.gameState.entityCounter;
    s.skillCooldowns = slot.gameState.skillCooldowns;
    s.pendingOrbitalStrikes = (slot.gameState.pendingOrbitalStrikes ?? []).map((strike) => ({
      ...strike,
      damage: strike.damage ?? 300,
    }));
    s.queuedGlobalSkills = (slot.gameState.queuedGlobalSkills ?? []).map((skill) => ({
      ...skill,
      ...(skill.skillId === "area_damage" ? { damage: skill.damage ?? 300 } : {}),
    }));
    s.pendingWaveReward = slot.gameState.pendingWaveReward;
    // Keep the current mapId on the engine (do not re-start).
    return true;
  }

  deleteSave(mapId: string): boolean {
    return _deleteSave(mapId);
  }

  listSaves(): Array<{
    mapId: string;
    savedAt: number;
    wave: number;
    gold: number;
    lives: number;
  }> {
    return _listSaves();
  }

  getSessionStats(): SessionStats {
    return _getStats();
  }

  recordGameResult(result: {
    status: GameState["status"];
    wave: number;
    goldEarned: number;
    enemiesKilled: number;
  }): void {
    if (this.replayMode) return;
    _recordGame(result);
  }

  resetSessionStats(): void {
    if (this.replayMode) return;
    _resetStats();
  }

  getTowersBuiltSession(): number {
    return this.engine.getTowersBuiltSession();
  }

  getLivesLostThisGame(): number {
    return this.engine.getLivesLostThisGame();
  }

  getWave1CompleteTime(): number {
    return this.engine.getWave1CompleteTime();
  }

  getElapsedTime(): number {
    return this.engine.getElapsedTime();
  }

  /** 获取引擎当前正在使用的地图 id；若尚未开始任何游戏则返回 null。 */
  getCurrentMapId(): string | null {
    return this.engine.getCurrentMapId();
  }

  /** 列出当前可供玩家选择的地图。 */
  getAvailableMaps(): Array<{ id: string; totalWaves: number }> {
    const map = this.content.maps.frontier_endless;
    return map ? [{ id: map.id, totalWaves: map.waves.length }] : [];
  }

  dispose(): void {
    this.disposed = true;
    for (const unsub of this.unsubs) unsub();
    this.unsubs = [];
  }

  snapshotNow(): HudSnapshot {
    const state = this.engine.state;
    const types = this.getTowerTypes();
    const selectedStillExists =
      this.selectedTowerId !== null &&
      state.towers.some((tower) => tower.id === this.selectedTowerId);
    if (!selectedStillExists) this.selectedTowerId = null;
    const selectedTower = this.selectedTowerId
      ? state.towers.find((tower) => tower.id === this.selectedTowerId)
      : undefined;
    const selectedTowerConfig = selectedTower ? this.content.towers[selectedTower.type] : undefined;
    const selectedTowerLevelStats =
      selectedTower && selectedTowerConfig
        ? getTowerLevelStats(selectedTowerConfig, selectedTower.level)
        : undefined;
    const waveEnemyCounts = this.engine.getWaveEnemyCounts();
    const activeAttackSpeedBoosts =
      selectedTower?.attackSpeedBoosts
        .filter((boost) => boost.remaining > 0)
        .map((boost) => ({ ...boost })) ?? [];
    const attackSpeedMultiplier = activeAttackSpeedBoosts.reduce(
      (factor, boost) => factor * boost.factor,
      1,
    );
    const selectedTowerStats = selectedTowerLevelStats
      ? {
          ...selectedTowerLevelStats,
          baseAttackSpeed: selectedTowerLevelStats.attackSpeed,
          attackSpeed: selectedTowerLevelStats.attackSpeed * attackSpeedMultiplier,
          attackSpeedMultiplier,
          activeAttackSpeedBoosts,
        }
      : undefined;
    return {
      gold: state.gold,
      lives: state.baseHp,
      maxLives: state.maxHp,
      wave: state.wave,
      totalWaves: state.totalWaves,
      isEndless: state.isEndless === true,
      waveInProgress: state.waveInProgress,
      waveEnemiesRemaining: waveEnemyCounts.remaining,
      waveEnemiesTotal: waveEnemyCounts.total,
      waveCountdown: state.waveCountdown,
      status: state.status,
      gameSpeed: state.gameSpeed,
      fps: this._fpsValue,
      skills: this.getSkillStates(),
      isDebug: this.engine.isDebugMode,
      unlimitedGold: this.engine.isDebugMode,
      enemyTypes: this.engine.isDebugMode ? this.getEnemyTypes() : [],
      towerTypes: types,
      towers: state.towers.map((t) => {
        const config = this.content.towers[t.type];
        const nextLevel = config?.upgrades.find((u) => u.level === t.level + 1);
        return {
          id: t.id,
          type: t.type,
          level: t.level,
          x: t.position.x,
          y: t.position.y,
          upgradeCost: nextLevel?.upgradeCost ?? null,
          buildCost: t.buildCost,
          upgradeCosts: t.upgradeCosts,
        };
      }),
      selectedTowerId: this.selectedTowerId,
      selectedTowerPos: this.selectedTowerId
        ? (() => {
            const tower = state.towers.find((t) => t.id === this.selectedTowerId);
            return tower ? { x: tower.position.x, y: tower.position.y } : null;
          })()
        : null,
      selectedEnemyId: this.selectedEnemyId,
      selectedEnemyHp: this.selectedEnemyId
        ? (() => {
            const enemy = state.enemies.find((e) => e.id === this.selectedEnemyId);
            return enemy ? { current: enemy.hp, max: enemy.maxHp } : undefined;
          })()
        : undefined,
      // 包含配置文件类型 ID，以便 UI 正确查找配置
      selectedTowerType: this.selectedTowerId
        ? (() => {
            const tower = state.towers.find((t) => t.id === this.selectedTowerId);
            return tower?.type ?? null;
          })()
        : null,
      selectedTowerLevel: this.selectedTowerId
        ? (() => {
            const tower = state.towers.find((t) => t.id === this.selectedTowerId);
            return tower?.level ?? undefined;
          })()
        : undefined,
      selectedTowerSkills:
        selectedTower && selectedTowerConfig
          ? (selectedTowerConfig.skillIds ?? []).flatMap((skillId) => {
              const skill = this.content.skills[skillId];
              if (!skill) return [];
              const condition = skill.releaseCondition;
              const progress = selectedTower.skillReleaseProgress[skillId] ?? 0;
              return [
                {
                  id: skill.id,
                  description: skill.skillDescription,
                  icon: skill.icon,
                  progress,
                  threshold: condition?.kind === "attack_count" ? condition.count : 0,
                  ready: condition?.kind === "attack_count" && progress >= condition.count,
                  active:
                    selectedTower.pendingAttackSkillId === skillId ||
                    selectedTower.attackSpeedBoosts.some(
                      (boost) => boost.skillId === skillId && boost.remaining > 0,
                    ),
                },
              ];
            })
          : [],
      selectedTowerStats,
      selectedTowerUpgradeCost:
        selectedTowerConfig && selectedTower
          ? (selectedTowerConfig.upgrades.find((u) => u.level === selectedTower.level + 1)
              ?.upgradeCost ?? null)
          : null,
      selectedEnemyType: this.selectedEnemyId
        ? (() => {
            const enemy = state.enemies.find((e) => e.id === this.selectedEnemyId);
            return enemy?.type ?? null;
          })()
        : null,
    };
  }

  private dispatch(command: GameCommand): void {
    if (this.replayMode) return;
    // 表面拒绝，使错误永远不会被静默吞掉（CLAUDE.md §18），但不重新抛出：
    // 被拒绝的命令（金币不足、位置无效、波次已在进行中）是正常游戏流程，
    // 从 DOM/Phaser 事件处理器中抛出会变成未处理的页面错误，可能导致渲染器崩溃。
    try {
      this.engine.dispatch(command);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.warn("[facade] command rejected:", message);
    }
  }
}
